import { type NextPage } from 'next'
import { withSurveys } from 'modules/credit-building/surveys-in-app/src/lib/with-surveys-page-config'
import { webviewRuntime } from 'modules/shared/page-enhancers/src/utils/webview-runtime'
import { type WithWebviewArgs, withWebviewPageConfig } from 'shared/page-enhancers'
import { isomorphicCookies } from 'shared/storage'

import { type FeatureFlagsAvailable } from '~/domains/platform/core/heimdall/types'
import { withIdentityScopes } from '~/domains/platform/core/night-owl/with-identity-scopes'
import { withVerifyIdentityOwnership } from '~/domains/platform/core/night-owl/with-verify-identity-ownership'
import { compose } from '~/domains/platform/lib/array-helpers/compose'

import { type OptIns, type WithOptInsProps, withDeusExMachina, withOptIns } from '../deus-ex-machina/ports'

import { withCreateAccountSituationGuard } from './compositional/with-create-account-situation-guard'
import { withLucyChat } from './compositional/with-lucy-chat'
import { type NavigationType, withNavigation } from './compositional/with-navigation'
import { withReceivableAccountsRedirect } from './compositional/with-receivable-accounts-redirect'
import { withRedirectToMobileOnSmallScreen } from './compositional/with-redirect-to-mobile'
import { withSwhRedirectConfig } from './compositional/with-swh-redirect'
import { withWhatsAppSupportButton } from './compositional/with-whatsapp-support-button'
import { withEntityKYC } from './with-entity-kyc'
import { withHeimdallGrantAccessPage } from './with-heimdall-grant-access-page'
import { withHeimdallSetFeatureFlags } from './with-heimdall-set-feature-flags'
import { withIdAssessment } from './with-id-assessment'
import { type PaymentProfileGateArg, withPaymentProfileGate } from './with-payment-profile-gate'
import { withPinRegister } from './with-pin-register'
import { withRBAC } from './with-rbac'
import { withRegisterName } from './with-register-name'

export interface PageConfig<T extends OptIns = never> {
  deusExMachina?: boolean
  entityKYC?: boolean
  skipEntityKYC?: boolean
  exMachinaOptIns?: T[]
  heimdallGrantAccessPage?: FeatureFlagsAvailable | FeatureFlagsAvailable[]
  heimdallSetFeatureFlags?: boolean
  idAssessment?: boolean
  identityScopes?: boolean
  isIdentityOwner?: boolean
  createAccountSituationGuard?: boolean
  navigation?: NavigationType
  paymentProfile?: PaymentProfileGateArg
  pinRegister?: boolean
  rbac?: boolean
  redirectToMobile?: boolean
  registerName?: boolean
  withReceivableAccountsRedirect?: 'v1' | 'v2'
  withSwhRedirectConfig?: string
  surveys?: boolean
  webview?: WithWebviewArgs
  showWhatsAppButton?: boolean
  lucyChat?: boolean
}

type PageConfigKey = keyof PageConfig

const isRedirectToMobile = Boolean(isomorphicCookies.getAll()['redirectToMobile'])

const defaultPageConfig: PageConfig = {
  deusExMachina: true,
  entityKYC: true,
  skipEntityKYC: false,
  exMachinaOptIns: [],
  heimdallGrantAccessPage: undefined,
  heimdallSetFeatureFlags: true,
  idAssessment: true,
  identityScopes: true,
  isIdentityOwner: true,
  createAccountSituationGuard: undefined,
  navigation: 'default',
  paymentProfile: false,
  pinRegister: true,
  rbac: true,
  redirectToMobile: !isRedirectToMobile,
  registerName: true,
  withReceivableAccountsRedirect: undefined,
  withSwhRedirectConfig: undefined,
  surveys: undefined,
  webview: undefined,
  showWhatsAppButton: true,
  lucyChat: true
}

const disabledDeusExMachinaConfig: PageConfig = {
  deusExMachina: false,
  entityKYC: false,
  skipEntityKYC: false,
  exMachinaOptIns: undefined,
  heimdallGrantAccessPage: undefined,
  heimdallSetFeatureFlags: false,
  idAssessment: false,
  identityScopes: false,
  isIdentityOwner: false,
  navigation: undefined,
  paymentProfile: false,
  pinRegister: false,
  rbac: false,
  redirectToMobile: false,
  withReceivableAccountsRedirect: undefined,
  withSwhRedirectConfig: undefined,
  showWhatsAppButton: true,
  lucyChat: false
}

const identity = <T,>(x: T) => x

const pageConfigHocMap: Record<keyof PageConfig, (x: any) => any> = {
  identityScopes: withIdentityScopes,
  isIdentityOwner: withVerifyIdentityOwnership,
  heimdallSetFeatureFlags: withHeimdallSetFeatureFlags,
  heimdallGrantAccessPage: withHeimdallGrantAccessPage,
  entityKYC: withEntityKYC,
  rbac: withRBAC,
  paymentProfile: withPaymentProfileGate,
  idAssessment: withIdAssessment,
  pinRegister: withPinRegister,
  redirectToMobile: withRedirectToMobileOnSmallScreen,
  navigation: withNavigation,
  registerName: withRegisterName,
  createAccountSituationGuard: withCreateAccountSituationGuard,
  deusExMachina: withDeusExMachina,
  exMachinaOptIns: withOptIns,
  withReceivableAccountsRedirect: withReceivableAccountsRedirect,
  withSwhRedirectConfig: withSwhRedirectConfig,
  surveys: withSurveys,
  webview: withWebviewPageConfig,
  skipEntityKYC: identity,
  showWhatsAppButton: identity,
  lucyChat: withLucyChat
}

function configFactory(pageConfig: PageConfig<OptIns>) {
  const shouldInjectWebviewDefaults = webviewRuntime.isWebView() && pageConfig.webview !== undefined

  const webviewOverrides: Partial<PageConfig> = shouldInjectWebviewDefaults
    ? {
        redirectToMobile: false,
        navigation: 'none'
      }
    : {}

  const config = (
    pageConfig.deusExMachina !== false
      ? { ...defaultPageConfig, ...pageConfig, ...webviewOverrides }
      : {
          ...disabledDeusExMachinaConfig,
          showWhatsAppButton: pageConfig.showWhatsAppButton ?? true
        }
  ) as PageConfig

  const hasHeaderNavigation =
    config.navigation === 'default' ||
    config.navigation === 'fullscreen' ||
    config.navigation === 'settings' ||
    config.navigation === 'settings-fullscreen' ||
    config.navigation === 'top-bar-only'

  const effectiveEntityKYC = Boolean(config.entityKYC) && !config.skipEntityKYC

  return {
    ifeHoc: (key: PageConfigKey) => {
      if (key === 'entityKYC') return effectiveEntityKYC ? pageConfigHocMap[key] : identity
      if (key === 'showWhatsAppButton')
        return config.showWhatsAppButton ? withWhatsAppSupportButton(hasHeaderNavigation) : identity
      return (config as any)[key] ? pageConfigHocMap[key] : identity
    },
    ifeHocWithArgs: (key: PageConfigKey) =>
      (config as any)[key] ? pageConfigHocMap[key]((config as any)[key]) : identity
  }
}

export type WithPageConfigProps<T extends OptIns = never> = WithOptInsProps<T>

export function withPageConfig<T extends OptIns = never, HasServerSideProps extends boolean = false>(
  Page: NextPage<HasServerSideProps extends true ? any : WithOptInsProps<T>>,
  pageConfig: PageConfig<T> = defaultPageConfig
): NextPage<WithOptInsProps<T>> {
  const { ifeHoc, ifeHocWithArgs } = configFactory(pageConfig)
  const search = typeof window !== 'undefined' ? window.location.search : ''
  const params = new URLSearchParams(search)
  const origin = params.get('origin')
  const isTon = origin === 'ton'
  if (isTon) {
    return withWebviewPageConfig({ timeoutMs: 10 * 60 * 1000, withAuth: true })(Page as any)
  }
  const WrappedPage = compose(
    ifeHoc('deusExMachina'),
    ifeHoc('registerName'),
    ifeHoc('entityKYC'),
    ifeHoc('pinRegister'),
    ifeHoc('idAssessment'),
    ifeHoc('heimdallSetFeatureFlags'),
    ifeHoc('identityScopes'),
    ifeHoc('isIdentityOwner'),
    ifeHoc('rbac'),
    ifeHoc('redirectToMobile'),
    ifeHoc('showWhatsAppButton'),
    ifeHoc('lucyChat'),
    ifeHoc('createAccountSituationGuard'),
    ifeHocWithArgs('heimdallGrantAccessPage'),
    ifeHocWithArgs('withReceivableAccountsRedirect'),
    ifeHocWithArgs('withSwhRedirectConfig'),
    ifeHocWithArgs('navigation'),
    ifeHocWithArgs('paymentProfile'),
    ifeHocWithArgs('exMachinaOptIns'),
    ifeHocWithArgs('surveys'),
    ifeHocWithArgs('webview')
  )(Page)

  WrappedPage.displayName = `withPageConfig(${Page.displayName})`

  return WrappedPage
}
